import { Box, ThemeProvider }                                                   from "@mui/material";
import React, { FC }                                                            from "react";
import ReactDOM                                                                 from "react-dom/client";
import { Provider }                                                             from "react-redux";
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from "react-router-dom";

import { AppErrorScreen, RouteErrorBoundary }                                   from "./components/layout/route-error-boundary";
import { AuthProvider }                                                         from "./contexts/auth-context";
import { SidebarProvider }                                                      from "./contexts/layout/sidebar-context";
import { NotificationProvider }                                                 from "./contexts/notification-context";
import { UnsavedChangesProvider }                                               from "./contexts/unsaved-changes-context";
import "./fonts.css";
import "./index.css";
import { mainTheme }                                       from "./mui-theme";
import { reportWebVitals }                                 from "./report-web-vitals";
import { setupStore }                                      from "./store/store";
import { importWithReload }                                from "./utils/lazy-with-reload";

import { CircularProgress }                                from "@mui/material";

// Lazy load common routes as a single chunk
// This will create a separate chunk
const initRouter = async () => {
  const { AuthRoutes } = await importWithReload(() => import("./routes/auth-routes"));
  const { TermsRoutes } = await importWithReload(() => import("./routes/terms-routes"));
  const { ClientRoutes } = await importWithReload(() => import("./routes/client-routes"));
  const { AdminRoutes } = await importWithReload(() => import("./routes/admin-routes"));
  const { PublicRoutes } = await importWithReload(() => import("./routes/public-routes"));

  // Pathless wrapper so every route falls back to our error screen instead of
  // React Router's default "Unexpected Application Error!".
  return createBrowserRouter(createRoutesFromElements(<Route errorElement={<RouteErrorBoundary />}>
    {PublicRoutes}
    {ClientRoutes}
    {AuthRoutes}
    {TermsRoutes}
    {AdminRoutes}
    <Route path="*" element={<div>404 - Page not found</div>} />
  </Route>),
    { hydrationData: { actionData: { "0-0": "child action" } } });
};

export const PageLoadingFallback: FC = () => (
  <Box sx={{
    display                     : "flex",
    justifyContent              : "center",
    alignItems                  : "center",
    height                      : "100vh",
    "@supports (height: 100svh)": { height: "100svh" },
  }}>
    <CircularProgress size={40} />
  </Box>
);

// Component that waits for router to load
const Router = () => {
  const [router, setRouter] = React.useState<Awaited<ReturnType<typeof initRouter>> | null>(null);
  const [error, setError] = React.useState<unknown>(null);

  React.useEffect(() => {
    initRouter().then(setRouter, setError);
  }, []);

  // Without this the app sat on the spinner forever when a route chunk failed.
  if (error) {
    return <AppErrorScreen error={error} />;
  }

  if (!router) {
    return (
      <PageLoadingFallback />
    );
  }

  return <RouterProvider router={router} />;
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(<React.StrictMode>
  <ThemeProvider theme={mainTheme}>
    <Provider store={setupStore()}>
      <AuthProvider>
        <SidebarProvider>
          <NotificationProvider>
            <UnsavedChangesProvider>
              <Router />
            </UnsavedChangesProvider>
          </NotificationProvider>
        </SidebarProvider>
      </AuthProvider>
    </Provider>
  </ThemeProvider>
</React.StrictMode>);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
